import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef, NgZone } from '@angular/core';
import { Router, RouterLink, RouterModule } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ApiSysEventsService } from '@features/resources/services/events.service';
import { LanguageService } from '@services/language/language.service';
import { ToastService } from '@services/toast/toast.service';

interface NotificationData {
  _id: string;
  moduleId: string;
  typeId: string;
  datetime: number;
  descriptionId: string;
  variable: string;
  status: string;
  usersID: number[];
  done: boolean;
  __v: number;
  description: string;
  module: string;
  type: string;
}

@Component({
  selector: 'app-notifications-menu',
  imports: [RouterModule],
  templateUrl: './notifications-menu.component.html',
  styleUrl: './notifications-menu.component.scss'
})
export class NotificationsMenuComponent implements OnInit, OnDestroy {
  @ViewChild('dropdownRef', { static: false }) dropdownRef!: ElementRef;

  isDropdownOpen = false;
  recentNotifications: NotificationData[] = [];
  unreadCount = 0;
  translateData: any = {};

  private destroy$ = new Subject<void>();
  private refreshInterval: any;

  constructor(
    private apiEvents: ApiSysEventsService,
    private langService: LanguageService,
    private toastService: ToastService,
    private router: Router,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.loadTranslations();
    this.loadNotifications();

    // Remove this in the future
    /*this.ngZone.runOutsideAngular(() => {
      this.refreshInterval = setInterval(() => {
        this.ngZone.run(() => {
          this.loadNotifications();
        });
      }, 30000);
    });*/
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.isDropdownOpen && this.dropdownRef) {
      const clickedInside = this.dropdownRef.nativeElement.contains(event.target);
      if (!clickedInside) {
        this.closeDropdown();
      }
    }
  }

  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      this.loadNotifications();
    }
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  private loadTranslations(): void {
    this.langService.getSharedDictionary('notifications-menu')
      .pipe(takeUntil(this.destroy$))
      .subscribe((data) => {
        this.translateData = data;
      });
  }

  private loadNotifications(): void {
    /*
    this.apiEvents.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: NotificationData[]) => {
          this.recentNotifications = data
            .sort((a, b) => b.datetime - a.datetime)
            .slice(0, 5);

          // Count unread notifications
          this.unreadCount = data.filter(notification => !notification.done).length;
        },
        error: (error) => {
          console.error('Error loading notifications:', error);
        }
      });
      */
  }

  markAsReadAndNavigate(notification: NotificationData): void {
    if (!notification.done) {
      this.apiEvents.changeNotificationStatusDone(notification._id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            notification.done = true;
            this.unreadCount = Math.max(0, this.unreadCount - 1);
          },
          error: (error) => {
            console.error('Error marking notification as read:', error);
          }
        });
    }
    this.closeDropdown();
    this.router.navigate(['/notifications']);
  }

  markAllAsRead(): void {
    this.apiEvents.getNotifications()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (allNotifications: NotificationData[]) => {
          const unreadNotifications = allNotifications.filter(n => !n.done);

          if (unreadNotifications.length === 0) {
            this.toastService.info('Info', this.translateData.all_notifications_already_read);
            return;
          }

          let completedRequests = 0;
          const totalRequests = unreadNotifications.length;

          unreadNotifications.forEach(notification => {
            this.apiEvents.changeNotificationStatusDone(notification._id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  const recentNotification = this.recentNotifications.find(n => n._id === notification._id);
                  if (recentNotification) {
                    recentNotification.done = true;
                  }

                  completedRequests++;

                  if (completedRequests === totalRequests) {
                    this.unreadCount = 0;
                    const message = this.translateData?.notifications_marked_as_read?.replace('{count}', totalRequests.toString()) 
                      || `${totalRequests} notifications marked as read`;
                    this.toastService.success(this.translateData?.success || 'Success', message);
                    this.loadNotifications();
                  }
                },
                error: (error) => {
                  console.error('Error marking notification as read:', error);
                  completedRequests++;

                  if (completedRequests === totalRequests) {
                    this.loadNotifications();
                  }
                }
              });
          });
        },
        error: (error) => {
          console.error('Error loading all notifications:', error);
          this.toastService.error(this.translateData?.error || 'Error', this.translateData?.failed_to_load_notifications || 'Failed to load notifications');
        }
      });
  }

  getNotificationIcon(type: string): string {
    switch (type?.toLowerCase()) {
      case 'coordination':
        return 'assets/icons/coordination.svg';
      case 'operation':
        return 'assets/icons/operation.svg';
      default:
        return 'assets/icons/notification-default.svg';
    }
  }

  formatTime(timestamp: number): string {
    const now = new Date().getTime();
    const diff = now - timestamp;

    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (minutes < 1) return this.translateData?.just_now || 'Just now';
    if (minutes < 60) return `${minutes}${this.translateData?.minutes_ago || 'm ago'}`;
    if (hours < 24) return `${hours}${this.translateData?.hours_ago || 'h ago'}`;
    if (days < 7) return `${days}${this.translateData?.days_ago || 'd ago'}`;

    return new Date(timestamp).toLocaleDateString();
  }

  truncateText(text: string, maxLength: number): string {
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  trackByNotificationId(index: number, notification: NotificationData): string {
    return notification._id;
  }
}